import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Store, TrendingUp, CircleCheck, Circle, Users } from "lucide-react";
import { getTodoStats } from "../../api/todos";
import { listBranches } from "../../api/branches";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Card, Input, Select, StatCard, Skeleton, EmptyState, Avatar } from "../../components/ui";
import { unwrapList, formatRate, formatDate, errorMessage, todayISO } from "../../lib/records";

// §9.8 — completion stats. Defaults to the last 7 days; the API caps the range
// at 92 days, which the date inputs enforce before a request is even made.
const MAX_RANGE_DAYS = 92;

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function rateTone(rate) {
  if (rate == null) return "bg-gray-200";
  if (rate >= 90) return "bg-emerald-500";
  if (rate >= 70) return "bg-amber-400";
  return "bg-red-400";
}

export default function TodoStats() {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";

  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(todayISO());
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!isChain) return;
    listBranches({ limit: 100 })
      .then((res) => setBranches(unwrapList(res, 100).items))
      .catch(() => setBranches([]));
  }, [isChain]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await getTodoStats({
        from,
        to,
        ...(isChain && branchId ? { partner: branchId } : {}),
      });
      setStats(res?.data ?? null);
    } catch (err) {
      setStats(null);
      setLoadError(errorMessage(err, "Stats could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [from, to, branchId, isChain]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const totals = stats?.totals;
  const byDate = stats?.byDate ?? [];
  const byEmployee = stats?.byEmployee ?? [];
  const peakExpected = Math.max(1, ...byDate.map((d) => d.expected ?? 0));

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8">
        <Link
          to="/todos"
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline transition-colors"
        >
          <ArrowLeft size={15} /> Back to daily tasks
        </Link>

        <PageHeader
          title="Task completion"
          subtitle="How reliably daily tasks are getting done, by day and by person."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              {isChain && (
                <div className="flex items-center gap-2">
                  <Store size={15} className="shrink-0 text-gray-400" />
                  <Select
                    value={branchId}
                    onChange={(e) => setBranchId(e.target.value)}
                    className="w-full sm:w-48 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                  >
                    <option value="">All branches</option>
                    {branches.map((b) => (
                      <option key={b._id} value={b._id}>
                        {b.name}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
              <Input
                type="date"
                value={from}
                max={to}
                onChange={(e) => setFrom(e.target.value)}
                title="From"
                className="w-36 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
              />
              <span className="text-sm text-gray-400">to</span>
              <Input
                type="date"
                value={to}
                min={from}
                max={todayISO()}
                onChange={(e) => setTo(e.target.value)}
                title="To"
                className="w-36 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
              />
            </div>
          }
        />

        {loadError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
            {loadError}
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="p-3 sm:p-5 bg-white/80 backdrop-blur-sm border border-emerald-100">
                <Skeleton className="h-3 sm:h-4 w-20 sm:w-24" />
                <Skeleton className="mt-2 sm:mt-3 h-6 sm:h-8 w-12 sm:w-16" />
              </Card>
            ))
          ) : (
            <>
              <StatCard label="Completion rate" value={formatRate(totals?.completionRate)} icon={TrendingUp} tone="primary" />
              <StatCard label="Done" value={totals?.done ?? 0} icon={CircleCheck} tone="green" />
              <StatCard label="Pending" value={totals?.pending ?? 0} icon={Circle} tone="orange" />
              <StatCard label="People tracked" value={totals?.employees ?? 0} icon={Users} tone="blue" />
            </>
          )}
        </div>

        {/* Per-day bars. `expected` counts what was on the list that day, so a
            task created later never counts against an earlier day. */}
        <Card className="mt-4 sm:mt-5 p-4 sm:p-5 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50">
          <h2 className="text-base sm:text-headline-sm font-semibold text-gray-800">By day</h2>
          <p className="text-xs sm:text-body-sm text-gray-500">
            {stats?.range
              ? `${formatDate(stats.range.from)} — ${formatDate(stats.range.to)} · ${stats.range.days} days`
              : "Loading…"}
          </p>

          {loading ? (
            <div className="mt-4 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-8" />
              ))}
            </div>
          ) : byDate.length === 0 ? (
            <div className="mt-2">
              <EmptyState icon={TrendingUp} title="Nothing recorded" description="No tasks were on the list in this range." />
            </div>
          ) : (
            <div className="mt-4 space-y-2.5">
              {byDate.map((d) => (
                <div key={d.date} className="flex items-center gap-3">
                  <span className="w-20 shrink-0 text-xs text-gray-500">{formatDate(d.date)}</span>
                  <div className="h-6 flex-1 overflow-hidden rounded-md bg-gray-100">
                    <div
                      className={`flex h-full items-center justify-end px-2 ${rateTone(d.completionRate)} transition-all duration-500`}
                      style={{ width: `${Math.max(4, ((d.done ?? 0) / peakExpected) * 100)}%` }}
                    >
                      <span className="text-[10px] font-bold text-white">{d.done ?? 0}</span>
                    </div>
                  </div>
                  <span className="w-24 shrink-0 text-right text-xs text-gray-500">
                    {d.done ?? 0}/{d.expected ?? 0} · {formatRate(d.completionRate)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Per-person leaderboard — the API returns it sorted by done, desc. */}
        <Card className="mt-4 sm:mt-5 p-4 sm:p-5 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50">
          <h2 className="text-base sm:text-headline-sm font-semibold text-gray-800">By person</h2>
          <p className="text-xs sm:text-body-sm text-gray-500">Most completed first.</p>

          {loading ? (
            <div className="mt-4 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : byEmployee.length === 0 ? (
            <div className="mt-2">
              <EmptyState icon={Users} title="No one to show" description="Assign a daily task to start tracking completion." />
            </div>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[560px] text-left">
                <thead>
                  <tr className="text-xs uppercase tracking-wide text-gray-500 border-b border-emerald-100">
                    <th className="px-2 py-2 font-semibold">Employee</th>
                    <th className="px-2 py-2 font-semibold">Tasks</th>
                    <th className="px-2 py-2 font-semibold">Done</th>
                    <th className="px-2 py-2 font-semibold">Pending</th>
                    <th className="px-2 py-2 font-semibold w-40">Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-100/50">
                  {byEmployee.map((row) => (
                    <tr key={row.employee?._id ?? row.employee} className="text-xs sm:text-body-sm text-gray-800">
                      <td className="px-2 py-2.5">
                        <div className="flex items-center gap-2">
                          <Avatar name={row.employee?.name ?? "?"} size={26} />
                          <div className="min-w-0">
                            <p className="truncate text-xs font-medium text-gray-800">{row.employee?.name ?? "Unknown"}</p>
                            {row.employee?.designation && (
                              <p className="truncate text-xs text-gray-500">{row.employee.designation}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2.5 text-gray-600">{row.todos ?? 0}</td>
                      <td className="px-2 py-2.5 font-semibold text-emerald-700">{row.done ?? 0}</td>
                      <td className="px-2 py-2.5 text-gray-600">{row.pending ?? 0}</td>
                      <td className="px-2 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                            <div
                              className={`h-full ${rateTone(row.completionRate)}`}
                              style={{ width: `${Math.min(100, row.completionRate ?? 0)}%` }}
                            />
                          </div>
                          <span className="w-12 shrink-0 text-right text-xs font-medium text-gray-600">
                            {formatRate(row.completionRate)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <p className="mt-4 text-xs text-gray-500">
          A day's <strong>done</strong> can exceed its <strong>expected</strong> when a task was deleted after
          being completed — deleted tasks drop out of expected while their past completions remain.
          Ranges are capped at {MAX_RANGE_DAYS} days.
        </p>
      </div>
    </div>
  );
}
